import React, { useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Divider,
  Drawer,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
  Popconfirm,
  Grid,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Credit, CreditPayment, LoanEvent } from "./types";
import AmortizationSchedule from "./AmortizationSchedule";
import AmortizationScheduleEditor from "./AmortizationScheduleEditor";
import LoanEventModal from "./LoanEventModal";
import {
  fetchLoanEvents,
  createLoanEvent,
  updateLoanEvent,
  deleteLoanEvent,
  fetchCreditDetails,
  updateCreditSchedule,
  ScheduleRowInput,
} from "./creditsService";
import CreditCurrencyConvertModal from "./CreditCurrencyConvertModal";

type Props = {
  open: boolean;
  credit: Credit | null;
  loading?: boolean;
  onClose: () => void;
  onEditCredit: (credit: Credit) => void;
  onAddPayment: (credit: Credit, payment?: CreditPayment) => void;
  onMarkInstallment: (creditId: string) => void;
  onDeletePayment: (creditId: string, paymentId: string) => void;
  onConfirmPayment: (
    creditId: string,
    paymentId: string,
    isPaid: boolean,
  ) => void;
  onRefreshCredit?: () => void;
  onCreditUpdated?: (credit: Credit) => void;
};

const { Title } = Typography;
const { useBreakpoint } = Grid;

const CreditDetailsDrawer: React.FC<Props> = ({
  open,
  credit,
  loading,
  onClose,
  onEditCredit,
  onAddPayment,
  onMarkInstallment,
  onDeletePayment,
  onConfirmPayment,
  onRefreshCredit,
  onCreditUpdated,
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  const isMobile = !screens.md; // < 768px

  const [events, setEvents] = useState<LoanEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<LoanEvent | null>(null);
  const [eventSubmitting, setEventSubmitting] = useState(false);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [scheduleEditing, setScheduleEditing] = useState(false);
  const [scheduleSaving, setScheduleSaving] = useState(false);

  const userData = useSelector((state: any) => state.userData.user);
  const token = userData?.accessToken;

  React.useEffect(() => {
    if (credit && credit._id && credit.useBankCalculation) {
      loadEvents();
    }
  }, [credit?._id, credit?.useBankCalculation]);

  const loadEvents = async () => {
    if (!credit?._id) return;

    setEventsLoading(true);
    try {
      const data = await fetchLoanEvents(credit._id, token);
      setEvents(data);
    } catch (error) {
      console.error("Failed to load events:", error);
      message.error(t("Failed to load events"));
    } finally {
      setEventsLoading(false);
    }
  };

  const handleAddEvent = () => {
    setCurrentEvent(null);
    setEventModalOpen(true);
  };

  const handleEditEvent = (event: LoanEvent) => {
    setCurrentEvent(event);
    setEventModalOpen(true);
  };

  const handleEventSubmit = async (values: any) => {
    if (!credit?._id) return;

    setEventSubmitting(true);
    try {
      const payload = {
        ...values,
        eventDate: values.eventDate
          ? dayjs(values.eventDate).toISOString()
          : undefined,
      };

      if (currentEvent) {
        await updateLoanEvent(credit._id, currentEvent._id, payload, token);
        message.success(t("Event updated successfully"));
      } else {
        await createLoanEvent(credit._id, payload, token);
        message.success(t("Event created successfully"));
      }

      setEventModalOpen(false);
      await loadEvents();
      if (onRefreshCredit) onRefreshCredit();
    } catch (error) {
      console.error("Failed to save event:", error);
      message.error(t("Failed to save event"));
    } finally {
      setEventSubmitting(false);
    }
  };

  const handleSaveSchedule = async (schedule: ScheduleRowInput[]) => {
    if (!credit?._id) return;
    setScheduleSaving(true);
    try {
      const updated = await updateCreditSchedule(credit._id, schedule, token);
      message.success(t("Schedule updated successfully"));
      setScheduleEditing(false);
      if (onCreditUpdated) onCreditUpdated(updated);
      if (onRefreshCredit) onRefreshCredit();
    } catch (err: any) {
      message.error(err?.response?.data?.message || t("Failed to update schedule"));
    } finally {
      setScheduleSaving(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!credit?._id) return;

    try {
      await deleteLoanEvent(credit._id, eventId, token);
      message.success(t("Event deleted successfully"));
      await loadEvents();
      if (onRefreshCredit) onRefreshCredit();
    } catch (error) {
      console.error("Failed to delete event:", error);
      message.error(t("Failed to delete event"));
    }
  };

  if (!credit) return null;

  const payments = credit.payments || [];
  const totalPaid = credit.paidAmount || 0;
  const remaining = credit.remainingAmount || 0;

  return (
    <Drawer
      title={t("Credit details")}
      width={isMobile ? "100%" : "min(1040px, 90vw)"}
      open={open}
      onClose={onClose}
      destroyOnClose
      extra={
        !isMobile && (
          <Space>
            <Button
              icon={<SwapOutlined />}
              onClick={() => setConvertModalOpen(true)}
            >
              {t("Convert currency")}
            </Button>
            <Button onClick={() => onEditCredit(credit)}>{t("Edit")}</Button>
            <Button type="primary" onClick={() => onAddPayment(credit)}>
              {t("Add manual payment")}
            </Button>
          </Space>
        )
      }
    >
      <Title level={4} style={{ marginBottom: 12 }}>
        {credit.name}
      </Title>

      {credit.useBankCalculation &&
        (!credit.annualInterestRate || credit.annualInterestRate === 0) &&
        (credit.totalWithInterest || 0) > (credit.totalAmount || 0) && (
          <Alert
            message={t("Warning: Interest rate is 0%")}
            description={t(
              "The annual interest rate for this credit is set to 0%. All interest calculations will be zero. Edit the credit to set a valid interest rate.",
            )}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

      <Descriptions column={1} size="small" bordered>
        <Descriptions.Item label={t("Currency")}>
          <Tag color="blue">{credit.currency || "EUR"}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("Total amount")}>
          {credit.totalAmount?.toLocaleString()}
        </Descriptions.Item>
        <Descriptions.Item label={t("Average interest rate")}>
          <Tag color="purple">{(credit.interestRate || 0).toFixed(2)}%</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("Interest amount")}>
          <span>
            {credit.interestAmount?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Descriptions.Item>
        {credit.totalInterest && credit.totalInterest > 0 && (
          <Descriptions.Item label={t("Total interest (amount)")}>
            <span>
              {credit.totalInterest?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("Total with interest")}>
          <span>
            {credit.totalWithInterest?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </Descriptions.Item>
        <Descriptions.Item label={t("Monthly rate")}>
          {credit.paymentType === "INSTALLMENTS"
            ? credit.monthlyAmount?.toLocaleString()
            : t("Manual")}
        </Descriptions.Item>
        <Descriptions.Item label={t("Paid / Remaining")}>
          {totalPaid.toLocaleString()} / {remaining.toLocaleString()}
        </Descriptions.Item>
        {credit.savedAmount && credit.savedAmount > 0 && (
          <Descriptions.Item label={t("Saved")}>
            <span style={{ color: "#52c41a", fontWeight: 600 }}>
              +
              {credit.savedAmount.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </span>
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("Loan approval date")}>
          {credit.startDate
            ? dayjs(credit.startDate).format("DD.MM.YYYY")
            : "-"}
        </Descriptions.Item>
        {credit.disbursementDate && (
          <Descriptions.Item label={t("Disbursement date")}>
            {dayjs(credit.disbursementDate).format("DD.MM.YYYY")}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={t("Mode / Type")}>
          <Space>
            <Tag color={credit.paymentMode === "AUTO" ? "blue" : "default"}>
              {credit.paymentMode === "AUTO" ? t("Auto") : t("Manual")}
            </Tag>
            <Tag color="purple">
              {credit.paymentType === "INSTALLMENTS"
                ? t("Installments")
                : t("Manual tracking")}
            </Tag>
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label={t("Status")}>
          <Tag color={credit.status === "COMPLETED" ? "green" : "gold"}>
            {t(credit.status)}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t("Manual override allowed")}>
          <Tag color={credit.allowManualOverride ? "green" : "red"}>
            {credit.allowManualOverride ? t("Yes") : t("No")}
          </Tag>
        </Descriptions.Item>
        {credit.locationId && typeof credit.locationId === "object" && (
          <Descriptions.Item label={t("Location")}>
            {credit.locationId.name}
          </Descriptions.Item>
        )}
        {credit.description && (
          <Descriptions.Item label={t("Description")}>
            {credit.description}
          </Descriptions.Item>
        )}
        {credit.useBankCalculation && (
          <>
            <Descriptions.Item label={t("Bank calculation")}>
              {credit.importedSchedule?.length
                ? <Tag color="purple">{t("Imported schedule")}</Tag>
                : <Tag color="blue">{t("Enabled")}</Tag>}
            </Descriptions.Item>
            <Descriptions.Item label={t("Annual interest rate (%)")}>
              {credit.annualInterestRate?.toFixed(2) || "0.00"}%
            </Descriptions.Item>
            {credit.effectiveAnnualRate && credit.effectiveAnnualRate > 0 && (
              <Descriptions.Item label={t("Effective annual rate (EKS)")}>
                <strong>{credit.effectiveAnnualRate?.toFixed(2)}%</strong>
                {credit.annualInterestRate &&
                  credit.effectiveAnnualRate > credit.annualInterestRate && (
                    <span
                      style={{
                        fontSize: "12px",
                        color: "#666",
                        marginLeft: "8px",
                      }}
                    >
                      (+
                      {(
                        credit.effectiveAnnualRate - credit.annualInterestRate
                      ).toFixed(2)}
                      % {t("costs")})
                    </span>
                  )}
              </Descriptions.Item>
            )}
            {!credit.importedSchedule?.length && (
              <>
                <Descriptions.Item label={t("Repayment method")}>
                  {credit.repaymentType === "ANNUITY"
                    ? t("Annuity")
                    : t("Declining balance")}
                </Descriptions.Item>
                <Descriptions.Item label={t("Day count convention")}>
                  {credit.dayCountConvention === "THIRTY_360"
                    ? "30/360"
                    : "Actual/365"}
                </Descriptions.Item>
                {(credit.gracePeriods || 0) > 0 && (
                  <>
                    <Descriptions.Item label={t("Grace periods")}>
                      {credit.gracePeriods} {t("months")}
                    </Descriptions.Item>
                    <Descriptions.Item label={t("Grace type")}>
                      {credit.graceType === "PAID_INTEREST"
                        ? t("Paid interest")
                        : t("Capitalized interest")}
                    </Descriptions.Item>
                  </>
                )}
              </>
            )}
          </>
        )}
      </Descriptions>

      {credit.paymentType === "INSTALLMENTS" && (
        <div style={{ marginTop: 12 }}>
          <Button
            block
            onClick={() => onMarkInstallment(credit._id)}
            disabled={credit.remainingAmount <= 0}
            loading={loading}
          >
            {t("Mark installment paid")}
          </Button>
        </div>
      )}

      <Divider />

      {credit.useBankCalculation && !credit.importedSchedule?.length && (
        <>
          <Title level={5} style={{ marginBottom: 8 }}>
            {t("Loan Events")}
          </Title>
          <Space
            direction="vertical"
            style={{ width: "100%", marginBottom: 16 }}
          >
            <Button
              type="dashed"
              block
              icon={<PlusOutlined />}
              onClick={handleAddEvent}
            >
              {t("Add event (fee, prepayment)")}
            </Button>

            {events.length > 0 && (
              <Table
                dataSource={events}
                rowKey="_id"
                loading={eventsLoading}
                size="small"
                pagination={false}
                columns={[
                  {
                    title: t("Type"),
                    dataIndex: "eventType",
                    key: "eventType",
                    width: 100,
                    render: (type: string) => (
                      <Tag
                        color={
                          type === "FEE"
                            ? "orange"
                            : type === "PREPAYMENT"
                              ? "green"
                              : "blue"
                        }
                      >
                        {t(type)}
                      </Tag>
                    ),
                  },
                  {
                    title: t("Date"),
                    dataIndex: "eventDate",
                    key: "eventDate",
                    width: 100,
                    render: (date: string) => dayjs(date).format("DD.MM.YYYY"),
                  },
                  {
                    title: t("Amount"),
                    dataIndex: "amount",
                    key: "amount",
                    width: 100,
                    align: "right" as const,
                    render: (amount: number) => amount.toLocaleString(),
                  },
                  {
                    title: "",
                    key: "actions",
                    width: 60,
                    render: (_, event: LoanEvent) => (
                      <Space size="small">
                        <Button
                          type="link"
                          size="small"
                          onClick={() => handleEditEvent(event)}
                        >
                          {t("Edit")}
                        </Button>
                        <Popconfirm
                          title={t("Delete this event?")}
                          onConfirm={() => handleDeleteEvent(event._id)}
                        >
                          <Button
                            type="link"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </Space>
                    ),
                  },
                ]}
              />
            )}
          </Space>
          <Divider />
        </>
      )}

      {credit.useBankCalculation &&
        credit.schedule &&
        credit.schedule.length > 0 && (
          <>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 24,
              }}
            >
              <Typography.Title level={5} style={{ margin: 0 }}>
                {t("Amortization Schedule")}
              </Typography.Title>
              {!scheduleEditing && (
                <Button size="small" onClick={() => setScheduleEditing(true)}>
                  {t("Edit schedule")}
                </Button>
              )}
            </div>
            {scheduleEditing ? (
              <AmortizationScheduleEditor
                initialSchedule={
                  credit.importedSchedule?.length ? credit.importedSchedule : credit.schedule
                }
                saving={scheduleSaving}
                onSave={handleSaveSchedule}
                onCancel={() => setScheduleEditing(false)}
              />
            ) : (
              <AmortizationSchedule
                schedule={credit.schedule.map((row, index) => ({
                  period: row.periodIndex || index,
                  date: row.dueDate,
                  openingBalance:
                    index === 0
                      ? (credit.importedSchedule?.length
                          ? credit.totalAmount || 0
                          : 0)
                      : credit.schedule?.[index - 1]?.remainingPrincipal || 0,
                  principal: row.principalPaid || 0,
                  interest: row.interestPaid || 0,
                  installment: row.installment || 0,
                  fees: row.otherDisbursements || 0,
                  prepayments: row.otherPayments || 0,
                  closingBalance: row.remainingPrincipal || 0,
                  events: row.description || "",
                }))}
              />
            )}
            <Divider />
          </>
        )}

      <Title level={5} style={{ marginBottom: 8 }}>
        {t("Payments")}
      </Title>
      <Table
        size="small"
        dataSource={payments}
        rowKey={(item) => item._id}
        pagination={{ pageSize: 6 }}
        columns={[
          {
            title: t("Paid"),
            dataIndex: "isPaid",
            align: "center" as const,
            width: 80,
            render: (isPaid: boolean, record: CreditPayment) => {
              const today = dayjs();
              const paymentDate = dayjs(record.paymentDate);
              const isDue =
                paymentDate.isBefore(today, "day") ||
                paymentDate.isSame(today, "day");

              return (
                <Tooltip title={isPaid ? t("Confirmed paid") : t("Not paid")}>
                  <Checkbox
                    checked={isPaid}
                    disabled={!isDue}
                    onChange={(e) =>
                      onConfirmPayment(credit._id, record._id, e.target.checked)
                    }
                    style={{
                      color: isPaid ? "#52c41a" : isDue ? "#ff4d4f" : "#d9d9d9",
                    }}
                  />
                </Tooltip>
              );
            },
          },
          {
            title: t("Date"),
            dataIndex: "paymentDate",
            render: (value: string) => dayjs(value).format("DD.MM.YYYY"),
          },
          {
            title: t("Amount"),
            dataIndex: "amount",
            render: (value: number) =>
              value?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }),
          },
          {
            title: t("Type"),
            dataIndex: "type",
            render: (value: string) => (
              <Tag color={value === "AUTO" ? "blue" : "geekblue"}>
                {value === "AUTO" ? t("Auto") : t("Manual")}
              </Tag>
            ),
          },
          {
            title: t("Note"),
            dataIndex: "note",
            ellipsis: true,
            render: (val: string) => val || "-",
          },
          {
            title: t("Actions"),
            render: (_: any, record: CreditPayment) => (
              <Space>
                {record.type !== "AUTO" && (
                  <Button
                    size="small"
                    onClick={() => onAddPayment(credit, record)}
                  >
                    {t("Edit")}
                  </Button>
                )}
                {record.type !== "AUTO" && (
                  <Button
                    danger
                    size="small"
                    onClick={() => onDeletePayment(credit._id, record._id)}
                  >
                    {t("Delete")}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />

      {/* Mobile action buttons */}
      {isMobile && (
        <div
          style={{
            position: "sticky",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px 0",
            background: "#fff",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            gap: "8px",
            marginTop: "16px",
            flexWrap: "wrap",
          }}
        >
          <Button
            style={{ flex: 1 }}
            icon={<SwapOutlined />}
            onClick={() => setConvertModalOpen(true)}
          >
            {t("Convert")}
          </Button>
          <Button style={{ flex: 1 }} onClick={() => onEditCredit(credit)}>
            {t("Edit")}
          </Button>
          <Button
            style={{ flex: 1 }}
            type="primary"
            onClick={() => onAddPayment(credit)}
          >
            {t("Add payment")}
          </Button>
        </div>
      )}

      <LoanEventModal
        open={eventModalOpen}
        loading={eventSubmitting}
        event={currentEvent}
        installmentsCount={credit.installmentsCount}
        onCancel={() => setEventModalOpen(false)}
        onSubmit={handleEventSubmit}
      />

      <CreditCurrencyConvertModal
        open={convertModalOpen}
        credit={credit}
        token={token}
        onCancel={() => setConvertModalOpen(false)}
        onSuccess={(updated) => {
          setConvertModalOpen(false);
          if (onCreditUpdated) onCreditUpdated(updated);
        }}
      />
    </Drawer>
  );
};

export default React.memo(CreditDetailsDrawer);
